import React, { useState } from 'react';
import { Brain, Loader2, MessageSquare, Sparkles, Star, Trash2 } from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { apiFetch } from '../../../lib/apiBase';
import { toast } from 'sonner';

const FeedbackTab = ({
  loading,
  feedbackItems,
  setFeedbackItems,
  aiInsights,
  analyzing,
  runAIAnalysis,
}) => {
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Delete this feedback? This cannot be undone.')) return;
    setDeletingId(feedbackId);
    try {
      const res = await apiFetch(`/feedback/${feedbackId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Feedback deleted');
        setFeedbackItems(prev => prev.filter(f => (f.feedback_id || f.id) !== feedbackId));
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err?.detail || 'Failed to delete feedback');
      }
    } catch (e) {
      toast.error('Failed to delete feedback');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-aqua animate-spin" />
        </div>
      ) : (
        <>
          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-aqua" />
              Recent Member Feedback
            </h3>
            {feedbackItems.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No feedback yet</p>
            ) : (
              <div className="space-y-4">
                {feedbackItems.map((item) => {
                  const id = item.feedback_id || item.id;
                  return (
                    <div key={id} className="p-4 bg-[#16161a] rounded-xl">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{item.member_name || item.member}</p>
                          <p className="text-gray-500 text-xs">{new Date(item.created_at || item.date).toLocaleDateString()} • {item.category}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star key={star} className={`w-4 h-4 ${star <= item.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
                            ))}
                          </div>
                          <Badge className={
                            item.sentiment === 'positive' ? 'bg-green-500/10 text-green-400' :
                            item.sentiment === 'negative' ? 'bg-red-500/10 text-red-400' :
                            'bg-yellow-500/10 text-yellow-400'
                          }>
                            {item.sentiment}
                          </Badge>
                          <button
                            onClick={() => handleDelete(id)}
                            disabled={deletingId === id}
                            className="p-1.5 text-gray-500 hover:text-red-400 transition-colors disabled:opacity-50"
                            title="Delete feedback"
                          >
                            {deletingId === id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm">"{item.feedback}"</p>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-aqua" />
                AI Feedback Analysis
              </h3>
              <Button
                onClick={runAIAnalysis}
                disabled={analyzing}
                className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {analyzing ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </div>

            {aiInsights ? (
              <div className="space-y-6">
                <div className="p-4 bg-aqua/10 border border-aqua/20 rounded-xl">
                  <h4 className="font-semibold text-aqua mb-2">Summary</h4>
                  <p className="text-white">{aiInsights.summary}</p>
                </div>

                {aiInsights.objectives && aiInsights.objectives.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-white mb-3">Actionable Objectives</h4>
                    <div className="space-y-3">
                      {aiInsights.objectives.map((obj, idx) => (
                        <div key={idx} className="p-4 bg-[#16161a] rounded-xl border-l-4 border-l-aqua">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={
                              obj.priority === 'High' ? 'bg-red-500/10 text-red-400' :
                              obj.priority === 'Medium' ? 'bg-yellow-500/10 text-yellow-400' :
                              'bg-gray-500/10 text-gray-400'
                            }>
                              {obj.priority}
                            </Badge>
                          </div>
                          <p className="text-white font-medium">{obj.objective}</p>
                          <p className="text-gray-400 text-sm mt-1">→ {obj.action}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {aiInsights.trends && aiInsights.trends.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-white mb-3">Key Trends</h4>
                    <div className="flex flex-wrap gap-2">
                      {aiInsights.trends.map((trend, idx) => (
                        <Badge key={idx} className="bg-[#16161a] text-gray-300 border-[#2a2a32]">
                          {trend}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {aiInsights.sentiment_breakdown && (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-500/10 rounded-xl text-center">
                      <p className="text-green-400 text-2xl font-bold">{aiInsights.sentiment_breakdown.positive || 0}</p>
                      <p className="text-gray-400 text-sm">Positive</p>
                    </div>
                    <div className="p-4 bg-yellow-500/10 rounded-xl text-center">
                      <p className="text-yellow-400 text-2xl font-bold">{aiInsights.sentiment_breakdown.mixed || 0}</p>
                      <p className="text-gray-400 text-sm">Mixed</p>
                    </div>
                    <div className="p-4 bg-red-500/10 rounded-xl text-center">
                      <p className="text-red-400 text-2xl font-bold">{aiInsights.sentiment_breakdown.negative || 0}</p>
                      <p className="text-gray-400 text-sm">Negative</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Click "Run Analysis" to get AI-powered insights from member feedback</p>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default FeedbackTab;
